import { NoddiIcon } from 'noddi-ui-common';
import { extractUserInitialsFromName } from 'noddi-util';
import { Link } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '../../../base/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../../base/dropdown-menu';
import { useSidebar } from '../../../base/sidebar/sidebar-context';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../base/tooltip';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from './Sidebar';
import { useMenuTooltip } from './useMenuTooltip';

type UserInfo = {
  name: string;
  email: string;
  avatarUrl?: string;
};

type NoddiSidebarFooterProps = {
  onLogout?: () => void;
  profilePath: string;
  profileLabel: string;
  logoutLabel: string;
  user: UserInfo;
  devSettingsPath?: string;
  extraMenuItems?: React.ReactNode;
};

export function NoddiSidebarFooter({
  onLogout,
  profilePath,
  profileLabel,
  logoutLabel,
  user,
  devSettingsPath,
  extraMenuItems
}: NoddiSidebarFooterProps) {
  const { isMobile, setOpenMobile, state } = useSidebar();
  // `state` tracks the desktop sidebar; the mobile sheet always shows full rows.
  const isCollapsed = state === 'collapsed' && !isMobile;
  const { menu, content, tooltip } = useMenuTooltip();

  const closeIfMobile = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  const handleLogout = () => {
    closeIfMobile();
    onLogout?.();
  };

  const userInitials = extractUserInitialsFromName(user.name);

  const triggerButton = (
    <DropdownMenuTrigger asChild>
      <SidebarMenuButton
        size='lg'
        // aria-expanded, not data-state: when collapsed, the tooltip trigger overrides data-state on the same element
        className='aria-expanded:bg-sidebar-accent aria-expanded:text-sidebar-accent-foreground'
      >
        <Avatar className='size-8 rounded-lg'>
          <AvatarImage src={user.avatarUrl} alt={`${user.name} avatar`} />
          <AvatarFallback className='rounded-lg'>{userInitials}</AvatarFallback>
        </Avatar>

        {/* min-w-0 lets long names truncate instead of pushing the chevron out of the row */}
        <div className='grid min-w-0 flex-1 gap-0.5 text-left leading-tight'>
          <span className='truncate font-semibold'>{user.name}</span>
          <span className='truncate text-xs text-muted-foreground'>{user.email}</span>
        </div>

        <NoddiIcon name='ChevronsUpDown' className='ml-auto text-muted-foreground' />
      </SidebarMenuButton>
    </DropdownMenuTrigger>
  );

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu modal={false} {...menu}>
          {isCollapsed ? (
            <Tooltip {...tooltip}>
              <TooltipTrigger asChild>{triggerButton}</TooltipTrigger>
              <TooltipContent side='right' className='border border-border bg-white text-foreground shadow-md'>
                {user.name}
              </TooltipContent>
            </Tooltip>
          ) : (
            triggerButton
          )}

          <DropdownMenuContent
            side={isCollapsed ? 'right' : 'top'}
            align={isCollapsed ? 'end' : 'center'}
            sideOffset={isCollapsed ? 8 : 4}
            // The collapsed trigger is icon-wide; match it only when there is room for the labels
            className={isCollapsed ? 'min-w-56' : 'w-(--radix-popper-anchor-width)'}
            {...content}
          >
            <DropdownMenuItem asChild onSelect={closeIfMobile}>
              <Link to={profilePath}>
                <NoddiIcon name='User' />
                <span>{profileLabel}</span>
              </Link>
            </DropdownMenuItem>

            {extraMenuItems}

            {devSettingsPath && (
              <DropdownMenuItem asChild onSelect={closeIfMobile}>
                <Link to={devSettingsPath}>
                  <NoddiIcon name='Settings' />
                  <span>Dev settings</span>
                </Link>
              </DropdownMenuItem>
            )}

            {onLogout && (
              <DropdownMenuItem onSelect={handleLogout}>
                <NoddiIcon name='LogOut' />
                <span>{logoutLabel}</span>
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
